import { useEffect, useState } from 'react';
import { Button, Field, Input, Select } from '../ui';
import { Dialog } from './Dialog';
import { SubscriptionsAPI } from '../../services/endpoints/admin';
import { useDispatchAction } from '../../hooks/useDispatchAction';

/**
 * Gift a complimentary subscription to a recipient. When `lockedEmail` is
 * provided the recipient field is prefilled and read-only (e.g. opened from a
 * specific user row); otherwise the email is free-form.
 */
export function GiftDialog({
  open,
  onClose,
  onGifted,
  lockedEmail,
}: {
  open: boolean;
  onClose: () => void;
  onGifted: () => void;
  lockedEmail?: string;
}) {
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState('MONTHLY');
  const [days, setDays] = useState(30);
  const { run, busy } = useDispatchAction();

  // Reset the form whenever the dialog (re)opens, prefilling the locked email.
  useEffect(() => {
    if (open) {
      setEmail(lockedEmail ?? '');
      setPlan('MONTHLY');
      setDays(30);
    }
  }, [open, lockedEmail]);

  // Keep Days in step with the chosen Plan so the plan label and the granted
  // access length stay consistent; the admin can still override Days after.
  function changePlan(next: string) {
    setPlan(next);
    setDays(next === 'ANNUAL' ? 365 : 30);
  }

  async function gift() {
    const ok = await run('Gift subscription', () => SubscriptionsAPI.gift(email.trim(), plan, days));
    if (ok !== null) onGifted();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Gift subscription"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="ember" onClick={gift} disabled={busy || !email.trim()}>Send gift</Button>
        </>
      }
    >
      <Field label="Recipient email">
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} readOnly={!!lockedEmail} autoFocus={!lockedEmail} />
      </Field>
      <div className="nb-form-row nb-form-row--two">
        <Field label="Plan">
          <Select value={plan} onChange={e => changePlan(e.target.value)}>
            <option value="MONTHLY">Monthly</option>
            <option value="ANNUAL">Annual</option>
          </Select>
        </Field>
        <Field label="Days">
          <Input type="number" min={1} max={365} value={days} onChange={e => setDays(Number(e.target.value))} />
        </Field>
      </div>
    </Dialog>
  );
}
